import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import * as folderActions from '../../actions/folderActions';
import PageContainer from '../Global/Containers/PageContainer';
import MaxWidthContainer from '../Global/Containers/MaxWidthContainer';
import FoldersList from './FoldersList';
import ModalContainer from '../Global/Modals/ModalContainer';
import CreateNewModal from '../Global/Modals/CreateNewModal';
import Button from '../Global/Buttons/Button';
import Icon from '../Global/Icons/Icon';
import IconWithText from '../Global/Icons/IconWithText';
import EditFolderModal from './EditFolderModal';
import EditFolderForm from './EditFolderForm';
import ShareFolderModal from "./ShareFolderModal";
import DeleteItemModal from "./DeleteItemModal";
import MoveAndReplaceFolderModal from "./MoveAndReplaceFolderModal";
import FolderSearchBar from "./FolderSearchBar";
import FolderFilterDropdown from "./FolderFilterDropdown";
import {
  allFoldersFilterOption,
  folderFilterOptions,
  getFolderFilterFunc,
  levelFolderFunc,
  ownedFoldersFilterOption,
  searchFolderFunc,
  sharedWithFoldersFilterOption,
  sortByTitle,
} from "../../utils/organiseFolders";

class FoldersPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFolders: [],
      activeItem: ``,
      newFolderModalOpen: false,
      newFolderName: ``,
      newFolderModalSubmitting: false,
      nameExistsInFolder: false,
      deleteItemModalOpen: false,
      deleteItemModalSubmitting: false,
      itemMoved: ``,
      shareFolderModalOpen: false,
      accessorSearch: ``,
      folderSearch: ``,
      filterDropdownActive: false,
      activeFilter: allFoldersFilterOption,
      shareAccessorId: null,
      shareFolderSubmitting: false,
      editFolderModalOpen: false,
      editFolderModalSubmitting: false,
      activeFolder: {},
      dragging: false,
      dragStart: null,
      draggingId: null,
      moveAndReplaceFolderModalOpen: false,
      moveAndReplaceFolderModalSubmitting: false,
      folderConflict: {},
    };
  }

  componentDidMount() {
    // If there is a query param in the URL to set a predefined plan type
    if (this.props.location.query.filter) {
      const filter = this.props.location.query.filter;
      if (filter === ownedFoldersFilterOption.value) {
        this.setState({ activeFilter: ownedFoldersFilterOption });
      } else if (filter === sharedWithFoldersFilterOption.value) {
        this.setState({ activeFilter: sharedWithFoldersFilterOption })
      }
    }
    if (this.props.location.query.search) {
      this.setState({ folderSearch: this.props.location.query.search });
    }
    if (this.props.location.query.folder) {
      const folders = this.props.location.query.folder;
      let activeFolders;
      if (typeof folders === `string`) {
        activeFolders = [Number(folders)];
      } else {
        activeFolders = folders.map(folder => Number(folder));
      }
      this.setActiveFolders(activeFolders);
    }
    if (this.props.location.query.content) {
      const [type, id] = this.props.location.query.content.split(`_`);
      this.setActiveItem({ type, id });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.folders.data.length === 0 && nextProps.folders.data.length !== 0) {
      // On page load
      if (this.state.activeFolders.length > 0) {
        // one or more folders are already selected - load their content
        const firstFolder = nextProps.folders.data.find((item) => {
          return item.id === this.state.activeFolders[0] && item.type === `folder`;
        });
        const areShared = !firstFolder.owned_by_me;
        this.props.actions.loadFolderV3(this.state.activeFolders, areShared ? 1 : 0)
          .then(() => {
            // load the folder accessors
            this.state.activeFolders.forEach(item => this.props.actions.loadCurrentFolderAccessors(item));

            if (this.state.activeItem) {
              // a piece of content is also selected - load this too
              const [contentType, contentId] = this.state.activeItem.split(`_`);
              return this.props.actions.getFolderContentData(contentId, contentType)
                .then(() => this.scrollIntoView());
            }
            return this.scrollIntoView();
          });
      }
    }
  }

  onDragStart = (e) => {
    const { draggableId, source } = e;
    this.setState({
      dragging: true,
      dragStart: this.getDragDropId(source.droppableId),
      draggingId: draggableId,
    });
  };

  onDragEnd = (e) => {
    this.setState({
      dragging: false,
      dragStart: null,
      draggingId: null,
    });
    const { destination, draggableId } = e;
    if (!destination) return null;

    // We're moving something
    this.setState({ itemMoved: draggableId });

    // If we're deleting something
    if (destination.droppableId === `delete`) {
      this.setState({ deleteItemModalOpen: true });
      return;
    }

    let newFolder = this.getDragDropId(destination.droppableId);
    if (newFolder === `0`) {
      newFolder = null;
    } else {
      newFolder = Number(newFolder);
    }

    const type = this.getDragDropType(draggableId);
    const id = this.getDragDropId(draggableId);
    if (type === `folder`) {
      // check folder with same title doesn't exist in new location
      const folderId = Number(id);
      const folder = this.props.folders.data.find(item => item.type === `folder` && item.id === folderId);
      const folderConflict = this.props.folders.data.find(item =>
        item.type === `folder` &&
        item.parent_id === newFolder && // folders within current location
        item.id !== folderId && // exclude the current folder
        item.title === folder.title, // shares the same title
      );
      if (folderConflict !== undefined) {
        // Ask to replace existing folder or not
        this.setState({
          moveAndReplaceFolderModalOpen: true,
          folderConflict,
        });
        return;
      }

      // change folder location
      this.props.actions.moveFolder(folderId, { parent_id: newFolder })
        .then(() => {
          // if folder we moved was 'active'
          if (this.state.activeFolders.includes(folderId)) {
            let newActiveFolders = [];
            if (newFolder !== null) {
              const folderIdx = this.state.activeFolders.findIndex(item => item === folderId);
              if (folderIdx !== -1) {
                newActiveFolders = this.state.activeFolders.slice(0, folderIdx);
              }
            }
            if (this.state.activeItem) {
              // does the folder move affect whether this content is displayed?
              const [contentType, objectId] = this.state.activeItem.split(`_`);
              const content = this.props.folders.data.find(
                item => item.type === contentType && item.id === Number(objectId),
              );
              if (content && !newActiveFolders.includes(content.parent_id)) {
                // content item no longer downstream of the displayed folders
                this.setActiveItem(``);
              }
            }
            this.setActiveFolders(newActiveFolders);
          }
        })
        .finally(() => {
          this.setState({ itemMoved: `` });
        });
    } else {
      // change content location
      this.props.actions.changeItemFolder(id, newFolder)
        .then(() => {
          // clear the active item if it was the one moved
          const itemString = this.getActiveItemString({ type, id });
          if (itemString === this.state.activeItem) {
            // clear the active item
            this.setActiveItem(``);
          }
        })
        .finally(() => {
          this.setState({ itemMoved: `` });
        });
    }
  };

  getDragDropType = (id) => {
    const parts = id.split(`-`);
    // return everything but the last part
    const typePieces = parts.slice(0, parts.length - 1);
    return typePieces.join(`-`);
  }

  getDragDropId = (id) => {
    const parts = id.split(`-`);
    // return the last part only
    return parts[parts.length - 1];
  }

  setActiveFolders = (activeFolders) => {
    this.setState({ activeFolders }, () => {
      this.scrollIntoView();
      this.updateQueryString();
    });
  }

  getActiveItemString = (item) => {
    return `${item.type}_${item.id}`;
  }

  setActiveItem = (item) => {
    const activeItem = item ? this.getActiveItemString(item) : ``;
    this.setState({ activeItem }, () => {
      this.scrollIntoView();
      this.updateQueryString();
    });
  }

  scrollIntoView = () => {
    if (this.props.folders.loading) return;

    const scrollContainer = this.scrollContainer;
    // always scroll to top
    let scrollOptions = {
      behavior: `smooth`,
      top: 0,
    };
    if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
      scrollOptions = { ...scrollOptions, left: scrollContainer.scrollWidth };
    }
    scrollContainer.scroll(scrollOptions);
  }

  handleItemClick = (item) => {
    const numFolders = this.state.activeFolders.findIndex(folder => folder === item.parent_id);
    const newFolders = [...this.state.activeFolders];
    newFolders.length = numFolders + 1;

    if (item.type === `folder`) {
      newFolders.push(item.id);
      this.setActiveFolders(newFolders);
      this.setActiveItem(``);
    } else {
      this.setActiveFolders(newFolders);
      this.setActiveItem(item);
    }

    if (item.info === `partial`) {
      if (item.type === `folder`) {
        this.props.actions.loadFolderV3([item.id], item.shared && !item.owned_by_me ? 1 : 0)
          .then(() => this.props.actions.loadCurrentFolderAccessors(item.id));
      } else {
        this.props.actions.getFolderContentData(item.id, item.type);
      }
    }
  }

  updateQueryString = () => {
    // Get the URL minus the query params
    let queryString = this.props.location.pathname;
    queryString = `${queryString}?`;
    if (this.state.activeFilter.value !== allFoldersFilterOption.value) {
      queryString = `${queryString}filter=${this.state.activeFilter.value}&`;
    }
    if (this.state.folderSearch) {
      queryString = `${queryString}search=${this.state.folderSearch}&`;
    }
    this.state.activeFolders.forEach((folderId) => {
      queryString = `${queryString}folder=${folderId}&`;
    });
    if (this.state.activeItem) {
      queryString = `${queryString}content=${this.state.activeItem}&`;
    }
    this.props.router.push(queryString);
  }

  handleNewFolderClick = (folder) => {
    this.setState({ activeFolder: folder, newFolderModalOpen: true });
  }

  handleNewFolderNameChange = (e) => {
    const nameExists = this.props.folders.data.filter(item =>
      item.type === `folder` &&
      item.parent_id === (this.state.activeFolder.id || null) &&
      item.title === e.target.value,
    );
    this.setState({ newFolderName: e.target.value, nameExistsInFolder: nameExists.length > 0 });
  }

  handleNewFolderSubmit = (e) => {
    e.preventDefault();
    this.setState({ newFolderModalSubmitting: true });
    this.props.actions.createFolderV2(this.state.activeFolder.id || null, this.state.newFolderName)
      .then(() => {
        this.setState({
          newFolderModalOpen: false,
          newFolderName: ``,
          nameExistsInFolder: false,
        });
        this.handleItemClick(this.props.folders.data.find(item => item.new));
      })
      .finally(() => {
        this.setState({
          newFolderModalSubmitting: false,
        });
      });
  }

  handleCloseNewFolderModal = () => {
    this.setState({
      newFolderModalOpen: false,
      newFolderName: ``,
      nameExistsInFolder: false,
    });
  }

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  }

  handleBackClick = () => {
    if (this.state.activeItem) {
      this.setActiveItem(``);
    } else {
      const newFolders = [...this.state.activeFolders];
      newFolders.pop();
      this.setActiveFolders(newFolders);
    }
  }

  handleShareFolderClick = (folder) => {
    this.setState({
      activeFolder: folder,
      shareFolderModalOpen: true,
      accessorSearch: ``,
    });
  }

  changeAccessorSearch = (e) => {
    const newAccessorSearch = e.target.value;
    this.setState({ accessorSearch: newAccessorSearch });
  }

  getAccessors = () => {
    if (this.state.accessorSearch) {
      return this.props.folders.availableAccessors.filter(
        accessor => accessor.name.toLowerCase().includes(this.state.accessorSearch.toLowerCase()),
      );
    }
    return this.props.folders.availableAccessors;
  }

  handleShareAccessorClick = (accessorId) => {
    this.setState({ shareAccessorId: accessorId });
  }

  handlePermissionSelect = (accessor, shareOption) => {
    this.setState({
      shareAccessorId: null,
      shareFolderSubmitting: true,
    });
    if (shareOption === `remove`) {
      this.props.actions.removeFolderShare(this.state.activeFolder.id, accessor)
        .finally(() => {
          this.setState({
            activeFolder: this.props.folders.data.find(item => item.id === this.state.activeFolder.id),
            shareFolderSubmitting: false,
          });
        });
    } else {
      this.props.actions.shareFolder(this.state.activeFolder.id, accessor, shareOption)
        .finally(() => {
          this.setState({
            activeFolder: this.props.folders.data.find(item => item.id === this.state.activeFolder.id),
            shareFolderSubmitting: false,
          });
        });
    }
  }

  handleCloseShareModal = () => {
    this.setState({
      shareAccessorId: null,
      shareFolderModalOpen: false,
    });
  }

  handleEditFolderClick = (folder) => {
    this.setState({ activeFolder: folder, editFolderModalOpen: true });
  }

  handleEditFolderSubmit = (values) => {
    if (this.state.activeFolder.title === values.title) {
      // no change - no need to submit data
      this.setState({ editFolderModalOpen: false });
      return;
    }
    this.setState({ editFolderModalSubmitting: true });
    this.props.actions.editFolder(this.state.activeFolder.id, { title: values.title })
      .then(() => {
        this.setState({ editFolderModalOpen: false });
      })
      .finally(() => {
        this.setState({ editFolderModalSubmitting: false });
      });
  }

  handleDeleteFolderSubmit = () => {
    this.setState({ editFolderModalSubmitting: true });
    this.props.actions.deleteFolder(this.state.activeFolder.id)
      .then(() => {
        this.setState({ editFolderModalOpen: false });
        const folderIdx = this.state.activeFolders.findIndex(item => item === this.state.activeFolder.id);
        if (folderIdx !== -1) {
          this.setActiveFolders(this.state.activeFolders.slice(0, folderIdx));
        }
      })
      .finally(() => {
        this.setState({ editFolderModalSubmitting: false });
      });
  }

  handleDeleteItemClick = () => {
    this.setState({ deleteItemModalSubmitting: true });
    const id = this.getDragDropId(this.state.itemMoved);
    const type = this.getDragDropType(this.state.itemMoved);

    if (type === `folder`) {
      this.props.actions.deleteFolder(id)
        .then(() => {
          this.setState({ deleteItemModalOpen: false, itemMoved: `` });
          // if folder we deleted was 'active' move back to it's parent
          const folderIdx = this.state.activeFolders.findIndex(item => item === id);
          if (folderIdx !== -1) {
            this.setActiveFolders(this.state.activeFolders.slice(0, folderIdx));
          }
        })
        .finally(() => {
          this.setState({ deleteItemModalSubmitting: false });
        });
    } else {
      this.props.actions.deleteItemFromFolder(id)
        .then(() => {
          this.setState({ deleteItemModalOpen: false, itemMoved: `` });
          const itemString = this.getActiveItemString({ type, id });
          if (itemString === this.state.activeItem) {
            // clear the active item
            this.setActiveItem(``);
          }
        })
        .finally(() => {
          this.setState({ deleteItemModalSubmitting: false });
        });
    }
  }

  handleDeleteItemModalCloseClick = () => {
    this.setState({
      itemMoved: ``,
      deleteItemModalOpen: false,
    });
  }

  handleReplaceFolderClick = () => {
    this.setState({ moveAndReplaceFolderModalSubmitting: true });
    const folderMovedId = Number(this.getDragDropId(this.state.itemMoved));
    this.props.actions.replaceFolder(folderMovedId, this.state.folderConflict)
      .then(() => {
        const folderIdx = this.state.activeFolders.findIndex(
          folderId => folderId === this.state.folderConflict.id,
        );
        if (folderIdx !== -1) {
          this.setActiveFolders(this.state.activeFolders.slice(0, folderIdx));
        }
        this.handleMoveAndReplaceFolderModalCloseClick();
      })
      .finally(() => {
        this.setState({ moveAndReplaceFolderModalSubmitting: false });
      });
  }

  handleMoveAndReplaceFolderModalCloseClick = () => {
    this.setState({
      moveAndReplaceFolderModalOpen: false,
    }, () => {
      // update on completion to prevent flickering
      this.setState({
        itemMoved: ``,
        folderConflict: {},
      });
    });
  }

  changeFolderSearch = (e) => {
    let newState = {
      folderSearch: e.target.value,
    };
    if (this.state.folderSearch === `` || e.target.value === ``) {
      // clear active items when we clear search
      newState = { ...newState, activeFolders: [], activeItem: `` };
    }
    this.setState(newState, () => {
      this.scrollIntoView();
      this.updateQueryString();
    });
  }

  clearFolderSearch = () => {
    this.setState({ folderSearch: ``, activeFolders: [], activeItem: ``}, () => {
      this.scrollIntoView();
      this.updateQueryString();
    });
  }

  onFilterClick = (dropdownActive) => {
    this.setState({ filterDropdownActive: dropdownActive });
  };

  onFilterSelect = (option) => {
    let state = {
      activeFilter: option,
      filterDropdownActive: !this.state.filterDropdownActive,
    };
    if (option.value !== allFoldersFilterOption.value && this.state.activeFolders.length > 0) {
      // check if currently selected active folders are in filtered data
      const firstActiveFolder = this.props.folders.data.find(item =>
        item.type === `folder` && item.id === this.state.activeFolders[0],
      );
      const folderFilterFunc = getFolderFilterFunc(option.value);
      if (firstActiveFolder && !folderFilterFunc(firstActiveFolder)) {
        // active folders cleared by filter
        state = { ...state, activeFolders: [], activeItem: `` };
      }
    }
    this.setState(state, () => {
      this.updateQueryString();
    });
  };

  setInputFocus = (input) => {
    if (input) {
      // timeout needed because of transition on modals
      setTimeout(() => {
        input.focus();
        // set cursor to end of value
        input.setSelectionRange(input.value.length, input.value.length);
      }, 50);
    }
  }

  render() {
    const { folders } = this.props;
    const {
      activeFolders,
      activeItem,
      newFolderModalOpen,
      newFolderName,
      nameExistsInFolder,
      newFolderModalSubmitting,
      deleteItemModalOpen,
      deleteItemModalSubmitting,
      itemMoved,
      editFolderModalOpen,
      editFolderModalSubmitting,
      activeFolder,
      shareFolderModalOpen,
      accessorSearch,
      shareAccessorId,
      moveAndReplaceFolderModalOpen,
      moveAndReplaceFolderModalSubmitting,
      folderConflict,
      folderSearch,
      filterDropdownActive,
      activeFilter,
      dragging,
    } = this.state;

    // active filter
    const folderFilterFunc = getFolderFilterFunc(activeFilter.value);

    let topLevelFunc;
    let criteria;
    if (folderSearch) {
      // search filter
      topLevelFunc = searchFolderFunc;
      criteria = folderSearch.toLowerCase();
    } else {
      // default filter
      topLevelFunc = levelFolderFunc;
      criteria = 0;
    }

    let topLevelFolders = folders.data.filter(item => {
      if (item.type === `folder`) {
        return folderFilterFunc(item) && topLevelFunc(item, criteria);
      }
      return false;
    });

    if (folderSearch) {
      // also need to sort the result
      topLevelFolders = topLevelFolders.sort(sortByTitle);
    }

    let hideDelete = false;
    if (!dragging) {
      if (activeFolders.length > 0) {
        // if within a folder shared with me - hide
        const firstActiveFolder = topLevelFolders.find(folder => folder.id === activeFolders[0]);
        hideDelete = firstActiveFolder ? !firstActiveFolder.owned_by_me : false;
      } else {
        // if no toplevel folders owned by me - hide
        const ownedFolder = topLevelFolders.find(folder => folder.owned_by_me);
        hideDelete = ownedFolder === undefined;
      }
    }

    const deleteBtnClasses = classnames(`folders-list__delete-btn btn btn--destructive border-radius-l`, {
      'folders-list__delete-btn--hide': hideDelete,
    });

    return (
      <DragDropContext
        onDragStart={this.onDragStart}
        onDragUpdate={this.onDragUpdate}
        onDragEnd={this.onDragEnd}
      >
        <div id="folders-page">
          <PageContainer classes="bg-white">
            <MaxWidthContainer>
              <h1 className="like-h2 m-t-5 m-b-1">Folders</h1>
              <p className="m-b-5 like-h5 color-grey max-width-paragraph">Organise sessions, practices, diagrams and other content into folders and share with your team.</p>

              <div className="m-b-3 d-flex align-items-center justify-content-space-between folder-navigation">
                <div className="d-flex align-items-center justify-content-space-between">
                  <Button onClick={this.handleBackClick} classes="btn--icon btn--tertiary border-radius-s" disabled={activeFolders.length <= 0}>
                    <Icon name="arrow-left" size="m" />
                  </Button>

                  <FolderFilterDropdown
                    onDropdownClick={this.onFilterClick}
                    dropdownActive={filterDropdownActive}
                    filterOptions={folderFilterOptions}
                    onFilterClick={this.onFilterSelect}
                    activeFilter={activeFilter}
                  />
                </div>


                <FolderSearchBar
                  searchValue={folderSearch}
                  onSearchChange={this.changeFolderSearch}
                  onClearClick={this.clearFolderSearch}
                />
              </div>

              <FoldersList
                folders={folders.data}
                foldersIdTree={folders.idTree}
                onFolderClick={this.handleItemClick}
                onItemClick={this.handleItemClick}
                activeFolders={activeFolders}
                activeItem={activeItem}
                onNewFolderClick={this.handleNewFolderClick}
                canShare={this.props.folders.availableAccessors.length > 0}
                onShareFolderClick={this.handleShareFolderClick}
                onEditFolderClick={this.handleEditFolderClick}
                parentRef={(parent) => { this.scrollContainer = parent; }}
                isDragging={this.state.dragging}
                dragStart={this.state.dragStart}
                draggingId={this.state.draggingId}
                itemMoved={itemMoved}
                loading={folders.loading}
                folderSearch={folderSearch}
                topLevelFolders={topLevelFolders}
              />
            </MaxWidthContainer>

            <Droppable droppableId="delete" isDropDisabled={hideDelete}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className={deleteBtnClasses}>
                  <IconWithText name="delete-all" size="m">Drag here to remove</IconWithText>
                </div>
              )}
            </Droppable>

            <ModalContainer
              isOpen={newFolderModalOpen}
              closeModal={this.handleCloseNewFolderModal}
              label="New Folder Modal"
              onAfterOpen={() => { this.setInputFocus(this.createInput)}}
            >
              <CreateNewModal
                contentType="folder"
                onSubmitClick={this.handleNewFolderSubmit}
                onCloseClick={this.handleCloseNewFolderModal}
                onInputChange={this.handleNewFolderNameChange}
                inputPlaceholder="Folder name e.g. Shooting practices"
                inputValue={newFolderName}
                nameExists={nameExistsInFolder}
                loading={newFolderModalSubmitting}
                inputRef={input => this.createInput = input}
                inputMaxLength={30}
              />
            </ModalContainer>

            <DeleteItemModal
              modalIsOpen={deleteItemModalOpen}
              loading={deleteItemModalSubmitting}
              onConfirmClick={this.handleDeleteItemClick}
              onCloseClick={this.handleDeleteItemModalCloseClick}
              itemToDelete={itemMoved}
            />

            <EditFolderModal
              modalIsOpen={editFolderModalOpen}
              onCloseClick={() => this.closeModal(`editFolderModalOpen`)}
              onAfterOpen={() => { this.setInputFocus(this.editInput)}}
            >
              <EditFolderForm
                submitting={editFolderModalSubmitting}
                folder={activeFolder}
                onSubmit={this.handleEditFolderSubmit}
                onDeleteFolderClick={this.handleDeleteFolderSubmit}
                inputRef={input => this.editInput = input}
                foldersInLocation={
                  folders.data.filter(item =>
                    item.type === `folder` &&
                    item.parent_id === activeFolder.parent_id && // folders within current location
                    item.id !== activeFolder.id, // exclude the current folder
                  )
                }
              />
            </EditFolderModal>

            <ShareFolderModal
              modalIsOpen={shareFolderModalOpen}
              onCloseClick={this.handleCloseShareModal}
              onSearchChange={this.changeAccessorSearch}
              searchValue={accessorSearch}
              folder={activeFolder}
              availableAccessors={this.getAccessors()}
              shareAccessorClick={this.handleShareAccessorClick}
              shareAccessorId={shareAccessorId}
              permissionSelect={this.handlePermissionSelect}
            />

            <MoveAndReplaceFolderModal
              modalIsOpen={moveAndReplaceFolderModalOpen}
              onCloseClick={this.handleMoveAndReplaceFolderModalCloseClick}
              onReplaceClick={this.handleReplaceFolderClick}
              folderConflict={folderConflict}
              loading={moveAndReplaceFolderModalSubmitting}
            />
          </PageContainer>
        </div>
      </DragDropContext>
    );
  }
}

FoldersPage.propTypes = {
  router: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  folders: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    folders: state.folders,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, folderActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FoldersPage);



// WEBPACK FOOTER //
// ./library/app/components/Folders/FoldersPage.js